import React, { useCallback, useState, useEffect } from 'react';
import { IATMDropdownProps } from 'shared-it-appmod-ui';
import { debounce } from 'lodash';
import { useEmployeeContext } from 'src/contexts/employee.context';
import { IEmployee } from 'src/models/employee.model';
import {
  getEmployeeOptions,
  getEmployeeStatus,
  getParsedEmployee,
  getParsedEmployeeInternal,
} from 'src/selectors/employee.selector';
import { actionTypes } from 'src/ducks/employee.duck';
import DropdownEmployeeView from './dropdown-employee.view';

export type IProps = Omit<IATMDropdownProps, 'value'> & {
  value?: Required<IEmployee['emp_no'] | IEmployee['emp_no'][]>;
  internal?: boolean;
  valueField?: keyof IEmployee;
  onLoad?: (data: IEmployee | IEmployee[]) => void;
};

const DropdownEmployee: React.FC<IProps> = ({
  onChange,
  internal = false,
  valueField = 'emp_no', // This will allow you to select what field you want to be the value
  onLoad,
  ...props
}) => {
  const { multiple, value: propsValue } = props;
  const { state, actions } = useEmployeeContext();

  const [options, setOptions] = useState<Partial<IEmployee>[]>([]);
  const [selected, setSelected] = useState<Partial<IEmployee>[]>([]);
  const [loading, setLoading] = useState(false);
  const [value, setValue] = useState<IProps['value']>(
    propsValue || multiple ? [] : undefined
  );

  const status = getEmployeeStatus(state, actionTypes.EMPLOYEE_USER_FETCH);

  useEffect(() => {
    if (propsValue) {
      const fetchEmployee = async (values) => {
        setLoading(true);

        const result = await actions.usersPOST({
          employeeNos: values as string[],
        });

        if (result && result.payload) {
          const list = (result.payload?.rows ?? []).map((employee) =>
            getParsedEmployee(employee)
          );

          setSelected(list);
          setOptions(list);

          if (onLoad) {
            onLoad(multiple ? list : list[0]);
          }
        }

        setLoading(false);
      };

      setValue(propsValue);

      if (!loading && !selected.length && propsValue.length && !status.error) {
        fetchEmployee(multiple ? propsValue : [propsValue]);
      }
    }
  }, [
    propsValue,
    multiple,
    actions,
    selected,
    setLoading,
    setValue,
    setSelected,
    setOptions,
    onLoad,
  ]); // eslint-disable-line

  const handleSearch = useCallback(
    debounce(async (keyword: string) => {
      setLoading(true);
      let list: Partial<IEmployee>[] = [];

      if (internal) {
        const result = await actions.searchInternalGET(keyword);

        if (result && result.payload) {
          list = (result.payload.employees ?? []).map((employee) =>
            getParsedEmployeeInternal(employee)
          );
        }
      } else {
        const result = await actions.searchGET(keyword);

        if (result && result.payload) {
          list = (result.payload ?? []).map((employee) =>
            getParsedEmployee(employee)
          );
        }
      }

      if (list.length) {
        setSelected((items) => {
          setOptions([...items, ...list]);

          return items;
        });
      }

      setLoading(false);

      return list;
    }, 300),
    [actions, internal, setLoading, setSelected, setOptions]
  );

  const handleChange = useCallback(
    (e, data) => {
      let values;

      if (multiple) {
        values = data.value.map((id) =>
          options.find((v) => v[valueField] === id)
        );

        setSelected(values);
      } else {
        values = options.find((v) => v[valueField] === data.value);

        if (values) {
          setSelected([values]);
        }
      }

      setValue(data.value);

      if (onChange) {
        onChange(e, data, values);
      }
    },
    [setValue, setSelected, onChange, multiple, options, valueField]
  );

  const optionList = [...selected, ...options].filter(Boolean);

  return (
    <DropdownEmployeeView
      {...props}
      loading={loading}
      onChange={handleChange}
      value={value}
      onSearchChange={(e) => {
        const keyword = e.target.value.trim();
        if (keyword) {
          handleSearch(keyword);
        }
      }}
      options={getEmployeeOptions(
        Array.from(new Set(optionList.map((a) => a.emp_no))).map((id) =>
          optionList.find((a) => a.emp_no === id)
        ),
        valueField
      )}
    />
  );
};

export default DropdownEmployee;
